import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Container from 'components/Container';
import SetPageNavAction from 'components/Portals/SetPageNavAction';

import { useTranslation } from 'services/localization';
import { getPlanPricingText } from '../utils';

import { push, Routes } from 'routing';

import { useSelector, useDispatch } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { RiStore2Line } from 'react-icons/ri';
import { logEvent } from 'common/services/analytics';
import { BILLING_CURRENCY } from 'common/modules/billing';
import { Callable } from 'common/frontend/callable';
import {
	getNewLocationSubscription,
	PlanDefinitions,
	getAddStoreSubscriptionPriceBreakdown,
} from 'common/modules/plans';
import PurchaseConfirmationCard, {
	ContentString,
	LineItem,
} from 'components/PurchaseConfirmationCard';
import ContainerBox from 'components/ContainerBox';
import moment from 'moment-timezone';
import { shouldChargeVat } from 'common/modules/billing/utils/vat';
import { isCorePlan } from 'common/modules/plans/plans';
import Banner from 'components/Banner';
import { getCreditsBalanceObject } from 'common/modules/billing/utils/credits';

const NewStore = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const plan = useSelector(ShopSelectors.shopPlan);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly)!;
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;

	const [loading, setLoading] = useState<boolean>(false);
	const [storeName, setStoreName] = useState<string>('');

	const nextBillingDate = shopReadOnly.features.plan.subscription?.upcomingBillingDate;
	const chargeVat = shouldChargeVat(shopPublicInfo.country, shopReadOnly.billing.vatNumber);
	const hasCustomBilling = shopReadOnly.billing.billingMethod === 'custom';

	const cardContent: ContentString[] = (() => {
		if (!plan) return [];
		const content = [
			{
				title: t('account.stores.plan', 'Plan'),
				content: PlanDefinitions[plan].name,
			},
			{
				title: t('account.stores.storePrice', 'Store price'),
				content: hasCustomBilling
					? t('common:billing.billingCycleCustom')
					: getPlanPricingText(shopFeatures?.plan),
			},
		];

		return content;
	})();

	const lineItems: LineItem[] = useMemo(() => {
		const plan = shopReadOnly.features.plan;
		const date = moment.utc();
		const newSubscription = getNewLocationSubscription({
			startDate: date,
			subscriptionDates: !!plan.subscription
				? {
						previousBillingDate: plan.subscription.previousBillingDate,
						upcomingBillingDate: plan.subscription.upcomingBillingDate,
				  }
				: undefined,
		});
		const priceBreakdown = getAddStoreSubscriptionPriceBreakdown({
			creditsBalance: getCreditsBalanceObject(shopReadOnly),
			shopReadOnly,
			plan,
			newSubscription,
			date,
		});

		return [
			{
				label: t('plans.billingBreakdown.subscriptionPrice', 'Subscription price'),
				amount: priceBreakdown.subscriptionPrice.value,
			},
			{
				label: t('plans.billingBreakdown.firstMonthDiscount', 'First month discount'),
				amount: (priceBreakdown.revenueDiscount.value + priceBreakdown.startDiscount.value) * -1,
			},
			{
				label: t('plans.billingBreakdown.creditsUsed', 'Credits used'),
				amount: priceBreakdown.creditsToUse.total * -1,
			},
		];
	}, [shopReadOnly, t]);

	const handlePurchase = async () => {
		logEvent('create_store_confirm');
		setLoading(true);
		try {
			await Callable.plans.subscriptions.addStore({
				merchantId: shopId,
				storeName,
			});
			dispatch(
				NotificationActions.showNotification({
					message: t('account.stores.storeCreated', 'Store created!'),
					variant: 'info',
				}),
			);
			push(Routes.ACCOUNT_STORES);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}

		setLoading(false);
	};

	if (!plan) {
		return null;
	}

	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{
					to: Routes.ACCOUNT_STORES,
					title: t('account.stores.pageTitle', 'Stores'),
					onClick: () => logEvent('create_store_cancel'),
				}}
				quickLinks={[]}
			/>
			<Container>
				<Box width="1000px" maxWidth="100%" margin="0 auto">
					{plan === 'CORE_COMMISSION' && (
						<Box mb={5}>
							<Banner
								variant="info"
								title={'Rentle pricing will be updated on June 5th'}
								description={
									'Rentle will have a new subscription-based pricing model starting June 5th, 2022. You will be automatically moved to one of our new plans, with a special discount, and your monthly subscription price will depend on the amount of stores you have. Check out our blog post on the update to learn more!'
								}
								button={{
									text: 'Learn more',
									onClick: () =>
										window.open('https://www.rentle.io/blog/news/new-pricing?hsLang=en', '_blank'),
								}}
							/>
						</Box>
					)}
					<Box className={classes.titleWrapper}>
						<Box className={classes.icon}>
							<RiStore2Line />
						</Box>
						<Typography variant="h6" className={classes.title}>
							{t('account.stores.newStore', 'New store')}
						</Typography>
					</Box>
					<ContainerBox>
						<Grid container spacing={2} sx={{ padding: 2 }}>
							<Grid item xs={12} md={6}>
								<TextField
									label={t('account.stores.storeName', 'Store name')}
									helperText={t(
										'account.stores.storeNameHelper',
										'Enter the name of your new store',
									)}
									placeholder={t('account.stores.storeNamePlaceholder', 'Side shop')}
									variant="outlined"
									fullWidth
									InputLabelProps={{ shrink: true }}
									value={storeName}
									onChange={(e) => setStoreName(e.target.value)}
								/>
							</Grid>
							<Grid item xs={12} md={1} />
							<Grid item xs={12} md={5}>
								<PurchaseConfirmationCard
									cardContent={cardContent}
									lineItems={lineItems}
									cta={{
										label: t('account.stores.confirmPurchase', 'Confirm purchase'),
										loading,
										onClick: handlePurchase,
									}}
									disabled={!!loading || !storeName}
									currency={BILLING_CURRENCY}
									chargeVat={chargeVat}
									nextBillingDate={nextBillingDate ? moment(nextBillingDate) : undefined}
									isFree={isCorePlan(plan) || hasCustomBilling}
									styling="inline"
								/>
							</Grid>
						</Grid>
					</ContainerBox>
				</Box>
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		titleWrapper: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			alignItems: 'center',
			width: 800,
			maxWidth: '100%',
			margin: theme.spacing(5, 0),
		},
		icon: {
			padding: theme.spacing(1.5),
			border: `2px solid ${theme.palette.border.paper}`,
			marginRight: theme.spacing(2),
			borderRadius: 4,
		},
		container: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
		},
		title: {
			fontSize: '2.8rem',
		},
		card: {
			background: theme.palette.background.paper,
			border: `1px solid ${theme.palette.border.paper}`,
			borderRadius: '4px',
			padding: theme.spacing(4),
			width: 800,
			maxWidth: '100%',
		},
		cardInfoRow: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: theme.spacing(3),
		},
		billingTitle: {
			textTransform: 'uppercase',
			color: theme.palette.text.disabled,
			fontSize: '1.4rem',
			fontWeight: 600,
			marginBottom: theme.spacing(3),
		},
		termsText: {
			fontSize: '1.2rem',
			lineHeight: '1.7rem',
			color: theme.palette.colors.tundra.main,
		},
		termsLink: {
			textDecoration: 'underline',
			color: theme.palette.primary.main,
		},
	}),
);

export default NewStore;
